import confirmDialog from '@/lib/confirmDialog.js';
import setPath from '@/lib/setPath';
import { getWorkflowAutosaves, restoreWorkflowAutosave } from '@/redux/actions/gravityActions';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import AutosavesDrawer from './AutosavesDrawer';

const WorkflowAutosavesProvider = ({ drawerKey = 'autosaves' }) => {
  const workflowId = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.id);
  const drawerState = useSelector((state) => state?.ui?.drawers?.[drawerKey]);
  const isOpen = drawerState?.open;
  const workflowAutosaves = useSelector(memoizeSelector('workflowAutosaves', (state) => state.workflow?.workflowAutosaves));
  const isLoading = useSelector((state) => state?.ui?.loadState?.getWorkflowAutosaves?.isLoading);

  useEffect(() => {
    if (workflowId && isOpen) {
      getWorkflowAutosaves({ workflowId, limit: 30 });
    }
  }, [workflowId, isOpen]);

  const filteredAutosaves = useMemo(() => {
    if (!workflowAutosaves) {
      return [];
    }
    return workflowAutosaves;
  }, [workflowAutosaves]);

  const handleRestore = useCallback(
    (autosave) => {
      return new Promise((resolve, reject) => {
        const doRestore = async () => {
          const confirmed = await confirmDialog({
            title: 'Restore Autosave',
            message: 'This will replace your current draft with the autosaved version. Your current draft will be saved as a snapshot. Continue?',
            confirm: 'Restore',
            confirmVariant: 'primary'
          });

          if (confirmed !== true) {
            reject(new Error('User cancelled'));
            return;
          }

          restoreWorkflowAutosave({ workflowId, autosaveId: autosave?.id }, (response) => {
            if (response?.success !== false) {
              setPath(`/workflows/${workflowId}/draft`);
              resolve(response);
            } else {
              reject(new Error(response?.error || 'Failed to restore autosave'));
            }
          });
        };

        doRestore();
      });
    },
    [workflowId]
  );

  return <AutosavesDrawer drawerKey={drawerKey} title="Autosaves" autosaves={filteredAutosaves} onRestore={handleRestore} isLoading={isLoading} />;
};

export default WorkflowAutosavesProvider;
